import {Injectable} from '@angular/core';
import {environment} from '../../../../../environments/environment';
import {restServices} from '../../../../rest-services';
import {
	BalanceRequest,
	CreateWalletRequest,
	DeleteWalletRequest,
	MonthTotalsRequest,
	MonthTotalsResponse,
	PaymentRequest,
	RechargeWalletRequest,
	RefundRequest,
	SetAlertThresholdRequest,
	TransactionHistoryRequest,
	TransactionType,
	Wallet,
	WalletSearchRequest,
	WalletTransactionLog
} from '../../../../shared/models';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {Observable, Subject} from 'rxjs';
import {shareReplay} from 'rxjs/operators';
import {JPAPagination} from '@shared-lib';

@Injectable({providedIn: 'root'})
export class WalletService {

	updateWalletData: Subject<void> = new Subject<void>();

	private serviceUrl: string = `${environment.baseApiUrl}${restServices.wallet}`;

	constructor(private http: HttpClient) {
	}

	createWallet(request: CreateWalletRequest): Observable<Wallet> {
		return this.http.post<Wallet>(`${this.serviceUrl}/create`, request);
	}

	deleteWallet(request: DeleteWalletRequest): Observable<void> {
		return this.http.post<void>(`${this.serviceUrl}/delete`, request);
	}

	rechargeWallet(request: RechargeWalletRequest): Observable<Wallet> {
		return this.http.post<Wallet>(`${this.serviceUrl}/recharge`, request);
	}

	getBalance(request: BalanceRequest): Observable<number> {
		return this.http.post<number>(`${this.serviceUrl}/balance`, request);
	}

	monthTotals(request: MonthTotalsRequest): Observable<MonthTotalsResponse> {
		return this.http.post<MonthTotalsResponse>(`${this.serviceUrl}/month-totals`, request);
	}

	getLastTransactionByType(idWallet: number, transactionType: TransactionType): Observable<WalletTransactionLog> {
		return this.http.get<WalletTransactionLog>(`${this.serviceUrl}/last-transaction-by-type/${idWallet}/${transactionType}`);
	}

	setAlertThreshold(request: SetAlertThresholdRequest): Observable<Wallet> {
		return this.http.post<Wallet>(`${this.serviceUrl}/alert-threshold`, request);
	}

	pay(request: PaymentRequest): Observable<Wallet> {
		return this.http.post<Wallet>(`${this.serviceUrl}/pay`, request);
	}

	refund(request: RefundRequest): Observable<Wallet> {
		return this.http.post<Wallet>(`${this.serviceUrl}/refund`, request);
	}

	getTransactions(request: TransactionHistoryRequest, pagination?: JPAPagination): Observable<HttpResponse<WalletTransactionLog[]>> {
		let httpParams = new HttpParams();
		if (pagination) {
			httpParams = httpParams.append('page', pagination.page).set('size', pagination.size);
		}
		if (pagination?.sort) {
			pagination.sort.forEach((val: string) => httpParams = httpParams.append('sort', val));
		}
		return this.http.post<WalletTransactionLog[]>(`${this.serviceUrl}/transactions`, request, {params: httpParams, observe: 'response'});
	}

	getTransactionsAdmin(request: TransactionHistoryRequest, pagination?: JPAPagination): Observable<HttpResponse<WalletTransactionLog[]>> {
		let httpParams = new HttpParams();
		if (pagination) {
			httpParams = httpParams.append('page', pagination.page).set('size', pagination.size);
		}
		if (pagination?.sort) {
			pagination.sort.forEach((val: string) => httpParams = httpParams.append('sort', val));
		}
		return this.http.post<WalletTransactionLog[]>(`${this.serviceUrl}/transactions-admin`, request,
			{params: httpParams, observe: 'response'});
	}

	getTransactionById(id: number): Observable<WalletTransactionLog> {
		return this.http.get<WalletTransactionLog>(`${this.serviceUrl}/transaction/${id}`);
	}

	getUsableWallets(idAzienda?: number): Observable<Wallet[]> {
		let params = new HttpParams();
		if (idAzienda) {
			params = params.set('idAzienda', idAzienda.toString());
		}
		return this.http.get<Wallet[]>(`${this.serviceUrl}/usable`, {params});
	}

	// N.B.: il servizio recupera SOLO wallet di tipo USER (personali)
	getWalletPersonaleByMail(mail: string): Observable<Wallet> {
		return this.http.get<Wallet>(`${this.serviceUrl}/admin/info-by-mail`, {params: {mail}});
	}

	// N.B.: il servizio recupera SOLO wallet di tipo COMPANY (aziendali)
	getWalletAziendale(idAzienda: number): Observable<Wallet> {
		return this.http.get<Wallet>(`${this.serviceUrl}/admin/wallet-aziendale`, {params: {idAzienda}});
	}

	// ricerca paginata dei borsellini per il pannello admin di gestione borsellini
	cercaBorselliniAdmin(request: WalletSearchRequest, pagination?: JPAPagination): Observable<HttpResponse<Wallet[]>> {
		let httpParams = new HttpParams();
		if (pagination) {
			httpParams = httpParams.append('page', pagination.page).set('size', pagination.size);
		}
		return this.http.post<Wallet[]>(`${this.serviceUrl}/admin/search`, request, {params: httpParams, observe: 'response'});
	}

	getUsableWalletsAdmin(idAzienda: number, mail: string): Observable<Wallet[]> {
		const params = new HttpParams().set('idAzienda', idAzienda.toString()).set('mail', mail);
		return this.http.get<Wallet[]>(`${this.serviceUrl}/admin/usable`, {params}).pipe(shareReplay(1));
	}

}
